import React, {
  MouseEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { NavigateFunction, useLocation, useParams } from "react-router-dom";
import { LoadingMessagePanel } from "../../components/loadingPanel";
import { ListItem } from "../../components/lists";
import { NavPanel, setBackButtonLocation } from "../../components/navPanel";
import styled from "styled-components";
import { Song } from "../../domain/song";
import { LoadStatus, LoadStatuses } from "../common/loading";
import { RegistrationForm } from "../../components/registrationForm";
import { RegisterUser } from "../../services/userService";
import { ReturnOrError, StatusCodes } from "../../services/common";

interface Songs {
  page: Song[];
}

type GetSongsForTagId = (id: string) => Promise<ReturnOrError<Songs>>;

const SongListPanelWrapper = styled.div`
  overflow-y: scroll;
  height: 83%;
`;

const songListScrollKey = (path: string) => `songListScroll:${path}`;

const SongListPanel = ({
  songs,
  navigator,
  listPath,
  restoreScroll,
  onRestoreScrollConsumed,
}: {
  songs: Song[];
  navigator: NavigateFunction;
  listPath: string;
  restoreScroll: boolean;
  onRestoreScrollConsumed?: () => void;
}) => {
  const songListPanel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scrollKey = songListScrollKey(listPath);
    const savedScroll = sessionStorage.getItem(scrollKey);
    if (restoreScroll && savedScroll) {
      const scrollTop = Number.parseInt(savedScroll);
      if (!Number.isNaN(scrollTop) && songListPanel.current) {
        songListPanel.current.scrollTop = scrollTop;
      }
    }
    sessionStorage.removeItem(scrollKey);
    if (restoreScroll) {
      onRestoreScrollConsumed?.();
    }
  }, [listPath, restoreScroll, onRestoreScrollConsumed]);

  return (
    <SongListPanelWrapper
      className={"SongListPage.SongListPanelWrapper"}
      ref={songListPanel}
    >
      {songs.map((song) => {
        const goToSong: MouseEventHandler = () => {
          sessionStorage.setItem(
            songListScrollKey(listPath),
            (songListPanel.current?.scrollTop || 0).toString(),
          );
          navigator(`/songs/${song.id}`);
        };
        return (
          <ListItem
            onClick={goToSong}
            role={"listitem"}
            key={`song::${song.id}`}
            aria-label={`song: ${song.title}`}
            data-id={song.id}
          >
            {song.title}
          </ListItem>
        );
      })}
    </SongListPanelWrapper>
  );
};

export const SongListView = ({
  getSongsForTagId,
  tagId,
  nav,
  registerUser,
  listPath,
  restoreScroll = false,
  onRestoreScrollConsumed,
}: {
  getSongsForTagId: GetSongsForTagId;
  tagId: string;
  nav: NavigateFunction;
  registerUser: RegisterUser;
  listPath: string;
  restoreScroll?: boolean;
  onRestoreScrollConsumed?: () => void;
}) => {
  const [loadStatus, setLoadStatus] = useState<LoadStatus<Songs>>(
    LoadStatuses.UNINITIALIZED,
  );

  useEffect(() => {
    if (loadStatus.name == LoadStatuses.UNINITIALIZED.name) {
      (async () => {
        setLoadStatus(LoadStatuses.LOADING);
        await loadSongList();
      })();
    }
  }, undefined);

  const loadSongList = async () => {
    const maybeSongsForTag = await getSongsForTagId(tagId);

    if (maybeSongsForTag.status == StatusCodes.REGISTRATION_REQUIRED) {
      setLoadStatus(LoadStatuses.REGISTRATION_REQUIRED);
    } else if (maybeSongsForTag.status == "OK" && maybeSongsForTag.value) {
      setLoadStatus({
        data: {
          page: maybeSongsForTag.value.page.sort((s1, s2) =>
            s1.title.toLowerCase() < s2.title.toLowerCase() ? -1 : 1,
          ),
        },
        name: "loaded",
      });
    }
  };

  if (
    loadStatus == LoadStatuses.LOADING ||
    loadStatus == LoadStatuses.UNINITIALIZED
  ) {
    return <LoadingMessagePanel />;
  } else if (loadStatus == LoadStatuses.REGISTRATION_REQUIRED) {
    return (
      <RegistrationForm
        registerUser={registerUser}
        onLogin={() => {
          setLoadStatus(LoadStatuses.UNINITIALIZED);
        }}
      />
    );
  } else {
    if (loadStatus.data) {
      return (
        <SongListPanel
          songs={loadStatus.data?.page}
          navigator={nav}
          listPath={listPath}
          restoreScroll={restoreScroll}
          onRestoreScrollConsumed={onRestoreScrollConsumed}
        />
      );
    } else {
      return <EmptyPanel />;
    }
  }
};

const defaultTag = "t:genre:ClassicPopRock";

export const SongListPage = ({
  registerUser,
  getSongsForTagId,
  nav,
}: {
  getSongsForTagId: GetSongsForTagId;
  nav: NavigateFunction;
  registerUser: RegisterUser;
}) => {
  const { tag } = useParams();
  const location = useLocation();
  if (!tag) {
    // This should not be possible, but handling, just in case
    console.error("No tag param was provided, redirecting to classic rock");
  }
  setBackButtonLocation(location.pathname);
  const restoreScroll =
    (
      location.state as {
        restoreSongListScroll?: boolean;
      } | null
    )?.restoreSongListScroll == true;
  const clearRestoreScrollState = useCallback(() => {
    nav(location.pathname, { replace: true, state: {} });
  }, [location.pathname, nav]);
  return (
    <>
      <SongListView
        getSongsForTagId={getSongsForTagId}
        tagId={tag || defaultTag}
        nav={nav}
        registerUser={registerUser}
        listPath={location.pathname}
        restoreScroll={restoreScroll}
        onRestoreScrollConsumed={clearRestoreScrollState}
      />
      <NavPanel nav={nav} />
    </>
  );
};

const EmptyPanel = () => {
  return (
    <>
      <div role={"note"} aria-label={"empty-songlist"} className="message">
        No songs have been loaded.
      </div>
    </>
  );
};
